import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const TestimonialsCarousel = () => {
    const { t } = useTranslation();
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    const testimonials = [
        {
            id: 1,
            name: t('testimonials.t1_name'),
            location: t('testimonials.t1_location'),
            text: t('testimonials.t1_text'),
            rating: 5,
            sessionType: t('testimonials.t1_type'),
            timeframe: t('testimonials.t1_time')
        },
        {
            id: 2,
            name: t('testimonials.t2_name'),
            location: t('testimonials.t2_location'),
            text: t('testimonials.t2_text'),
            rating: 5,
            sessionType: t('testimonials.t2_type'),
            timeframe: t('testimonials.t2_time')
        },
        {
            id: 3,
            name: t('testimonials.t3_name'),
            location: t('testimonials.t3_location'),
            text: t('testimonials.t3_text'),
            rating: 5,
            sessionType: t('testimonials.t3_type'),
            timeframe: t('testimonials.t3_time')
        },
        {
            id: 4,
            name: t('testimonials.t4_name'),
            location: t('testimonials.t4_location'),
            text: t('testimonials.t4_text'),
            rating: 5,
            sessionType: t('testimonials.t4_type'),
            timeframe: t('testimonials.t4_time')
        },
        {
            id: 5,
            name: t('testimonials.t5_name'),
            location: t('testimonials.t5_location'),
            text: t('testimonials.t5_text'),
            rating: 5,
            sessionType: t('testimonials.t5_type'),
            timeframe: t('testimonials.t5_time')
        }
    ];

    useEffect(() => {
        if (!isAutoPlaying) return;

        const interval = setInterval(() => {
            setCurrentTestimonial((prev) => 
                prev === testimonials.length - 1 ? 0 : prev + 1
            );
        }, 7000);

        return () => clearInterval(interval);
    }, [isAutoPlaying, testimonials.length]);

    const goToTestimonial = (index) => {
        setCurrentTestimonial(index);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 12000);
    };

    const nextTestimonial = () => {
        setCurrentTestimonial((prev) => 
            prev === testimonials.length - 1 ? 0 : prev + 1
        );
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 12000);
    };

    const prevTestimonial = () => {
        setCurrentTestimonial((prev) => 
            prev === 0 ? testimonials.length - 1 : prev - 1
        );
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 12000);
    };

    const current = testimonials[currentTestimonial];

    return (
        <section className="py-24 bg-gradient-to-b from-stone-50 via-warm-beige/30 to-stone-100/60 dark:from-stone-950 dark:via-stone-900 dark:to-stone-950 relative overflow-hidden transition-colors duration-300">
            {/* Ambient decorative elements */}
            <div className="absolute top-1/2 left-0 w-96 h-96 bg-terracotta/5 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>
            <div className="absolute top-1/2 right-0 w-96 h-96 bg-sage/5 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
                
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <span className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-terracotta-50 dark:bg-stone-800 text-terracotta dark:text-terracotta-light text-xs font-semibold tracking-wider uppercase mb-4 border border-terracotta-200/60 dark:border-stone-700">
                        Experiențe & Rezultate
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 dark:text-stone-100 mb-4 tracking-tight">
                        {t('testimonials.title')}
                    </h2>
                    <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg leading-relaxed">
                        {t('testimonials.subtitle')}
                    </p>
                </div>

                {/* Main Testimonial Card */}
                <div className="relative max-w-4xl mx-auto">
                    <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-12 lg:p-16 shadow-elevated border border-stone-200/80 dark:border-stone-800 relative transition-all duration-300">
                        
                        {/* Big quote mark decoration */}
                        <div className="absolute top-6 left-8 sm:top-8 sm:left-12 text-terracotta/15 dark:text-terracotta/25 font-display text-7xl sm:text-8xl select-none pointer-events-none leading-none">
                            “
                        </div>

                        {/* Stars & Tag Bar */}
                        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 relative z-10">
                            {/* 5 Stars */}
                            <div className="flex items-center space-x-1">
                                {[...Array(5)].map((_, i) => (
                                    <svg
                                        key={i}
                                        className="w-5 h-5 text-amber-500 fill-current"
                                        viewBox="0 0 20 20"
                                    >
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                            </div>

                            {/* Tags */}
                            <div className="flex items-center gap-2">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-terracotta-50 dark:bg-stone-800 text-terracotta dark:text-terracotta-light border border-terracotta-200/50 dark:border-stone-700">
                                    {current.sessionType}
                                </span>
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300">
                                    {current.timeframe}
                                </span>
                            </div>
                        </div>

                        {/* Testimonial Quote */}
                        <div className="relative z-10 min-h-[140px] sm:min-h-[120px] flex items-center">
                            <blockquote className="font-display italic text-lg sm:text-xl md:text-2xl text-stone-800 dark:text-stone-100 leading-relaxed font-normal">
                                "{current.text}"
                            </blockquote>
                        </div>

                        {/* Client Info & Nav Arrows */}
                        <div className="mt-8 pt-8 border-t border-stone-200/70 dark:border-stone-800 flex flex-col sm:flex-row items-center justify-between gap-6 relative z-10">
                            <div className="flex items-center space-x-3 text-center sm:text-left">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-terracotta to-amber-500 text-white font-bold flex items-center justify-center text-base shadow-soft flex-shrink-0">
                                    {current.name.charAt(0)}
                                </div>
                                <div>
                                    <div className="font-bold text-stone-900 dark:text-stone-100 text-base">
                                        {current.name}
                                    </div>
                                    <div className="text-xs text-stone-500 dark:text-stone-400">
                                        {current.location}
                                    </div>
                                </div>
                            </div>

                            {/* Navigation controls */}
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={prevTestimonial}
                                    className="p-3 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-terracotta hover:text-white dark:hover:bg-terracotta transition-colors shadow-xs"
                                    aria-label="Testimonial precedent"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                
                                {/* Dots */}
                                <div className="flex space-x-1.5 px-2">
                                    {testimonials.map((_, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => goToTestimonial(idx)}
                                            className={`h-2 rounded-full transition-all duration-300 ${
                                                idx === currentTestimonial 
                                                    ? 'w-6 bg-terracotta' 
                                                    : 'w-2 bg-stone-300 dark:bg-stone-700 hover:bg-stone-400'
                                            }`}
                                            aria-label={`Sari la testimonialul ${idx + 1}`}
                                        />
                                    ))}
                                </div>

                                <button
                                    onClick={nextTestimonial}
                                    className="p-3 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-terracotta hover:text-white dark:hover:bg-terracotta transition-colors shadow-xs"
                                    aria-label="Testimonial următor"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Experience Trust Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16 max-w-3xl mx-auto text-center">
                    <div className="bg-white/80 dark:bg-stone-900/80 backdrop-blur-sm p-6 rounded-2xl border border-stone-200/60 dark:border-stone-800 shadow-soft">
                        <div className="font-display text-3xl sm:text-4xl font-bold text-terracotta mb-1">500+</div>
                        <div className="text-xs sm:text-sm font-medium text-stone-600 dark:text-stone-400">
                            {t('testimonials.stats.peopleHelped') || 'Persoane ajutate'}
                        </div>
                    </div>

                    <div className="bg-white/80 dark:bg-stone-900/80 backdrop-blur-sm p-6 rounded-2xl border border-stone-200/60 dark:border-stone-800 shadow-soft">
                        <div className="font-display text-3xl sm:text-4xl font-bold text-terracotta mb-1">CPR</div>
                        <div className="text-xs sm:text-sm font-medium text-stone-600 dark:text-stone-400">
                            Acreditare Oficială
                        </div>
                    </div>

                    <div className="bg-white/80 dark:bg-stone-900/80 backdrop-blur-sm p-6 rounded-2xl border border-stone-200/60 dark:border-stone-800 shadow-soft">
                        <div className="font-display text-3xl sm:text-4xl font-bold text-terracotta mb-1">ISST</div>
                        <div className="text-xs sm:text-sm font-medium text-stone-600 dark:text-stone-400">
                            Certificare Internațională
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TestimonialsCarousel;
